import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { HttpError } from '@vagusx/clients';
import { useCreatePatientInEhr } from '@/hooks/use-patients';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UnsavedChangesDialog } from '@/components/custom/unsaved-changes-dialog';
import { DatePicker } from '@/components/custom/date-picker';
import { EntityCombobox } from '@/components/custom/entity-combobox';
import { registrationLocationPickerConfig } from '@/components/custom/entity-pickers';
import { toIsoDate } from '@/lib/date-range';

import {
  EMPTY,
  conflictFrom,
  formFieldOf,
  formSchema,
  isLookupField,
  toRequest,
  type Conflict,
  type FormValues,
} from './new-patient-form';

/**
 * Create a patient in the EHR from the Patients page. The server looks for the
 * person first; a match comes back as a 409 that this dialog turns into
 * "open that patient" or, for a possible match, "create anyway".
 */
/** Select value for "no sex given"; Radix Select items cannot use ''. */
const NO_SEX = 'unspecified';

export function NewPatientDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const navigate = useNavigate();
  const createPatient = useCreatePatientInEhr();
  const today = toIsoDate(new Date());

  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const form = useForm<FormValues>({ resolver: zodResolver(formSchema), defaultValues: EMPTY });
  const { errors, isDirty } = form.formState;
  const busy = createPatient.isPending;

  // A duplicate verdict is about the details that were looked up. Once the user edits
  // one of them it no longer applies — and "create anyway" must not carry it over to
  // details the server never checked. Other fields (location, address) leave it be.
  useEffect(() => {
    const sub = form.watch((_values, { name }) => {
      if (!name || isLookupField(name)) setConflict(null);
    });
    return () => sub.unsubscribe();
  }, [form]);

  const close = (): void => {
    form.reset(EMPTY);
    setConflict(null);
    onOpenChange(false);
  };

  const requestClose = (): void => {
    if (busy) return;
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    close();
  };

  const submit = (
    allowPossibleDuplicate: boolean
  ): ((e?: React.BaseSyntheticEvent) => Promise<void>) =>
    form.handleSubmit(async (values) => {
      setConflict(null);
      try {
        const patient = await createPatient.mutateAsync(toRequest(values, allowPossibleDuplicate));
        toast.success('Patient created in the EHR');
        close();
        navigate({ to: '/patients/$id', params: { id: patient.id } });
      } catch (err) {
        const found = conflictFrom(err);
        if (found) {
          setConflict(found);
          return;
        }
        if (err instanceof HttpError && err.isValidation && err.fieldErrors.length > 0) {
          let mapped = 0;
          for (const fe of err.fieldErrors) {
            const field = formFieldOf(fe.field);
            if (field) {
              form.setError(field, { message: fe.message });
              mapped++;
            }
          }
          if (mapped > 0) return;
        }
        toast.error(err instanceof Error ? err.message : 'Could not create the patient');
      }
    });

  const openPatient = (id: string): void => {
    close();
    navigate({ to: '/patients/$id', params: { id } });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : requestClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New patient</DialogTitle>
          <DialogDescription>
            Creates the patient in the EHR, after checking they are not already there.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit(false)} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" error={errors.firstName?.message}>
              <Input {...form.register('firstName')} autoFocus />
            </Field>
            <Field label="Last name" error={errors.lastName?.message}>
              <Input {...form.register('lastName')} />
            </Field>
            <Field label="Date of birth" error={errors.dateOfBirth?.message}>
              <DatePicker
                value={form.watch('dateOfBirth')}
                onChange={(v) =>
                  form.setValue('dateOfBirth', v, { shouldDirty: true, shouldValidate: true })
                }
                max={today}
                dropdowns
                placeholder="Pick a date"
              />
            </Field>
            <Field label="Sex">
              <Select
                value={form.watch('gender') ?? ''}
                onValueChange={(v) =>
                  form.setValue('gender', v === NO_SEX ? undefined : (v as 'female' | 'male'), {
                    shouldDirty: true,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select sex" />
                </SelectTrigger>
                <SelectContent>
                  {/* A mistaken choice must be undoable, not only swappable. */}
                  <SelectItem value={NO_SEX}>Not specified</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Phone" error={errors.phone?.message}>
              <Input {...form.register('phone')} inputMode="tel" />
            </Field>
            <Field label="Email" error={errors.email?.message}>
              <Input type="email" {...form.register('email')} />
            </Field>
          </div>

          <div className="grid grid-cols-6 gap-3">
            <Field label="Street" error={errors.address1?.message} className="col-span-4">
              <Input {...form.register('address1')} />
            </Field>
            <Field label="Apt / suite" className="col-span-2">
              <Input {...form.register('address2')} />
            </Field>
            <Field label="City" error={errors.city?.message} className="col-span-3">
              <Input {...form.register('city')} />
            </Field>
            <Field label="State" error={errors.state?.message} className="col-span-1">
              <Input {...form.register('state')} maxLength={2} className="uppercase" />
            </Field>
            <Field label="ZIP" error={errors.zip?.message} className="col-span-2">
              <Input {...form.register('zip')} inputMode="numeric" />
            </Field>
          </div>

          <Field
            label="Location"
            error={errors.locationId?.message}
            hint="The EHR department the patient is registered under."
          >
            <EntityCombobox
              config={registrationLocationPickerConfig}
              loadStrategy="preload"
              value={form.watch('locationId') || null}
              onChange={(id) =>
                form.setValue('locationId', id ?? '', { shouldDirty: true, shouldValidate: true })
              }
              clearable={false}
              placeholder="Select location"
              searchPlaceholder="Search locations…"
            />
          </Field>

          {conflict && (
            <ConflictPanel
              conflict={conflict}
              busy={busy}
              onOpen={openPatient}
              onCreateAnyway={() => void submit(true)()}
            />
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={requestClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || conflict !== null}>
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Create patient
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <UnsavedChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          close();
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
    </Dialog>
  );
}

function ConflictPanel({
  conflict,
  busy,
  onOpen,
  onCreateAnyway,
}: {
  conflict: Conflict;
  busy: boolean;
  onOpen: (id: string) => void;
  onCreateAnyway: () => void;
}): React.ReactElement {
  if (conflict.kind === 'existing') {
    const p = conflict.patient;
    return (
      <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
        <p className="flex items-center gap-1.5 font-medium text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          This patient already exists
        </p>
        <p className="text-amber-900">
          {p.firstName} {p.lastName}
          {p.dateOfBirth && <> · {formatDate(p.dateOfBirth)}</>}
          {p.ehrPatientId ? <> · EHR ID {p.ehrPatientId}</> : <> · not yet in the EHR</>}
        </p>
        <Button type="button" size="sm" variant="outline" onClick={() => onOpen(p.id)}>
          Open patient
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
      <p className="flex items-center gap-1.5 font-medium text-amber-900">
        <AlertTriangle className="h-4 w-4" />
        {conflict.matches.length === 1 ? 'A patient' : 'Patients'} who may be the same person
        already {conflict.matches.length === 1 ? 'exists' : 'exist'}
      </p>
      <ul className="space-y-0.5 text-amber-900">
        {conflict.matches.map((m) => (
          <li key={m.patientId ?? m.ehrPatientId} className="flex items-center gap-2">
            <span>
              {m.firstName} {m.lastName}
              {m.dateOfBirth && <> · {formatDate(m.dateOfBirth)}</>}
              {m.ehrPatientId ? <> · EHR ID {m.ehrPatientId}</> : <> · in VagusX only</>}
            </span>
            {m.patientId && (
              <Button
                type="button"
                size="sm"
                variant="link"
                className="h-auto p-0"
                onClick={() => onOpen(m.patientId!)}
              >
                Open
              </Button>
            )}
          </li>
        ))}
      </ul>
      <p className="text-xs text-amber-800">
        Check the EHR before continuing — a second chart for the same person has to be merged by
        hand.
      </p>
      <Button type="button" size="sm" variant="outline" onClick={onCreateAnyway} disabled={busy}>
        Create new patient anyway
      </Button>
    </div>
  );
}

function Field({
  label,
  error,
  hint,
  className,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={cn('grid gap-1.5', className)}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        hint && <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}
